'use client';

import { Accordion } from '@curology/ui-components-web/react/Accordion';
import { AccordionContent } from '@curology/ui-components-web/react/Accordion/AccordionContent';
import { AccordionSummary } from '@curology/ui-components-web/react/Accordion/AccordionSummary';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React, { useState } from 'react';

import { useDataProvider } from '@/components/flow/components/DataProvider/useDataProvider';
import { useProductSelectionAndCheckoutCommonData } from '@/components/product-selection/hooks/useProductSelectionAndCheckoutCommonData';
import ProductSelectionAndCheckoutLineItems from '@/components/productSelectionAndCheckoutLineItems';
import { DATA_KEYS } from '@/consts/dataKeys';
import { centsToDollarsAndCents } from '@/helpers/productSelectionAndCheckout/helpers';
import { type ItemPrices } from '@/types/productSelectionAndCheckout/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

const PriceSummary = ({
  itemPrices,
  trialTotalWithTax,
  trialTotalWithTaxAndWithoutDiscounts,
}: {
  itemPrices: ItemPrices;
  trialTotalWithTax: number;
  trialTotalWithTaxAndWithoutDiscounts: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const promotion = useDataProvider(DATA_KEYS.promotion);
  const { patient, fullSizeItemPrices } =
    useProductSelectionAndCheckoutCommonData();

  return (
    <div className="cur-bg-surface-neutral cur-p-4">
      <Accordion>
        <AccordionSummary
          data-sentry-id="Toggle price summary accordion"
          onClick={() => {
            const newIsOpen = !isOpen;

            setIsOpen(newIsOpen);

            trackEvent(
              newIsOpen
                ? AnalyticsEvent.CHECKOUT_PRICE_DROPDOWN_OPENED
                : AnalyticsEvent.CHECKOUT_PRICE_DROPDOWN_CLOSED,
              {
                offerId: patient?.introOffer?.id,
              },
            );
          }}
        >
          <div className="cur-flex cur-items-center cur-justify-between">
            <span>Today&apos;s total</span>
            <span className={promotion ? 'cur-text-right' : ''}>
              <span className={promotion ? '' : 'cur-mr-1'}>
                {centsToDollarsAndCents(trialTotalWithTax)}
              </span>
              {promotion ? (
                <Typography className="cur-text-brand" size="meta">
                  {promotion.percentageOffActual}% discount applied
                </Typography>
              ) : (
                trialTotalWithTax < trialTotalWithTaxAndWithoutDiscounts && (
                  <span className="cur-line-through cur-text-disabled">
                    {centsToDollarsAndCents(
                      trialTotalWithTaxAndWithoutDiscounts,
                    )}
                  </span>
                )
              )}
            </span>
          </div>
        </AccordionSummary>
        <AccordionContent className="cur-pt-4">
          <ProductSelectionAndCheckoutLineItems
            itemPrices={itemPrices}
            page="checkout"
            subscriptionItemPrices={fullSizeItemPrices}
          />
        </AccordionContent>
      </Accordion>
    </div>
  );
};

export default PriceSummary;
